import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, Badge, Modal } from 'react-bootstrap';
import ReactMarkdown from 'react-markdown';

import PageContentModal from './PageContentModal';
import { groupCitationsBySources, parseCitations } from './moUtils';
import { fetchMoConversationByDbq } from '../../../services/medicalOpinion';

const CITATION_REGEX_GLOBAL = /(\(source:\s*[^,]+,\s*pages?\s*\d+(?:-\d+)?(?:,\s*date\s*[\d-]+)?\))/gi;

const formatDateTime = (value) => {
  if (!value) return 'Unknown';
  try {
    return new Date(value).toLocaleString();
  } catch (e) {
    return String(value);
  }
};

const parseMetadata = (metadata) => {
  if (!metadata) return null;
  if (typeof metadata === 'string') {
    try {
      return JSON.parse(metadata);
    } catch (e) {
      return null;
    }
  }
  return metadata;
};

const getLatestMetadata = (messages, key) => {
  if (!Array.isArray(messages)) return null;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const meta = parseMetadata(messages[i]?.metadata);
    if (meta && meta[key]) return meta[key];
  }
  return null;
};

const getLatestAssistantContent = (messages) => {
  if (!Array.isArray(messages)) return '';
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const msg = messages[i];
    if (msg?.role === 'assistant' && String(msg?.content || '').trim()) {
      return String(msg.content).trim();
    }
  }
  return '';
};

const toSortedContextPages = (file) => {
  if (!Array.isArray(file?.topPages)) return [];
  return file.topPages
    .map((page) => ({
      pageNumber: Number(page?.pageNumber),
      fileId: file?.fileId || null,
      score: typeof page?.score === 'number' ? page.score : null,
    }))
    .filter((page) => Number.isInteger(page.pageNumber) && page.pageNumber > 0)
    .sort((a, b) => a.pageNumber - b.pageNumber);
};

const buildContextSources = (contextFiles = []) => {
  if (!Array.isArray(contextFiles)) return [];
  return contextFiles
    .filter((file) => file?.fileName || file?.fileId)
    .map((file) => ({
      fileName: file?.fileName || 'Unknown file',
      fileId: file?.fileId || null,
      pages: toSortedContextPages(file),
    }));
};

const mergeSources = ({ citationSources = [], contextSources = [] }) => {
  if (!citationSources.length && !contextSources.length) return [];
  if (!citationSources.length) return contextSources;
  if (!contextSources.length) return citationSources;

  const mergedByName = new Map();
  citationSources.forEach((source) => {
    const key = String(source?.fileName || '').toLowerCase();
    mergedByName.set(key, {
      fileName: source?.fileName || 'Unknown file',
      fileId: source?.fileId || null,
      pages: Array.isArray(source?.pages) ? [...source.pages] : [],
    });
  });

  contextSources.forEach((source) => {
    const key = String(source?.fileName || '').toLowerCase();
    const existing = mergedByName.get(key);
    if (!existing) {
      mergedByName.set(key, {
        fileName: source.fileName,
        fileId: source.fileId,
        pages: [...source.pages],
      });
      return;
    }

    if (!existing.fileId && source.fileId) {
      existing.fileId = source.fileId;
    }

    source.pages.forEach((page) => {
      if (!existing.pages.some((p) => p.pageNumber === page.pageNumber)) {
        existing.pages.push(page);
      }
    });
    existing.pages.sort((a, b) => a.pageNumber - b.pageNumber);
  });

  return Array.from(mergedByName.values());
};

const MoEvidenceModal = ({ show, onHide, claimId, userDbqId, autoMoData }) => {
  const [showPageModal, setShowPageModal] = useState(false);
  const [selectedPage, setSelectedPage] = useState({
    fileId: null,
    fileName: null,
    pageNumber: null,
  });

  const autoMoText =
    autoMoData?.remarksOverrideText ||
    autoMoData?.fullText ||
    autoMoData?.rawText ||
    autoMoData?.cleanedText ||
    '';
  const autoContextFiles = useMemo(
    () => (Array.isArray(autoMoData?.contextFiles) ? autoMoData.contextFiles : []),
    [autoMoData?.contextFiles],
  );
  const shouldFetchConversationFallback = Boolean(
    show &&
      userDbqId &&
      (!autoMoText || autoContextFiles.length === 0),
  );
  const { data: fallbackConversationData } = useQuery({
    queryKey: ['mo-evidence-fallback', userDbqId],
    queryFn: () => fetchMoConversationByDbq(userDbqId),
    enabled: shouldFetchConversationFallback,
    refetchOnWindowFocus: false,
    staleTime: 30 * 1000,
  });
  const fallbackMessages = useMemo(
    () => (Array.isArray(fallbackConversationData?.messages) ? fallbackConversationData.messages : []),
    [fallbackConversationData?.messages],
  );
  const fallbackContextFiles = useMemo(
    () => {
      const latestContextFiles = getLatestMetadata(fallbackMessages, 'contextFiles');
      return Array.isArray(latestContextFiles) ? latestContextFiles : [];
    },
    [fallbackMessages],
  );
  const fallbackMoText = useMemo(
    () =>
      fallbackConversationData?.conversation?.finalMedicalOpinion ||
      getLatestAssistantContent(fallbackMessages) ||
      '',
    [fallbackConversationData?.conversation?.finalMedicalOpinion, fallbackMessages],
  );
  const moText = autoMoText || fallbackMoText;
  const contextFiles = autoContextFiles.length > 0 ? autoContextFiles : fallbackContextFiles;

  const citations = useMemo(
    () => parseCitations(moText, contextFiles),
    [moText, contextFiles],
  );
  const citationSources = useMemo(
    () => groupCitationsBySources(citations),
    [citations],
  );
  const contextSources = useMemo(() => {
    return buildContextSources(contextFiles);
  }, [contextFiles]);
  const sources = useMemo(() => {
    return mergeSources({ citationSources, contextSources });
  }, [citationSources, contextSources]);

  const hasEvidenceData = Boolean(
    moText || sources.length > 0 || contextFiles.length > 0,
  );

  const contentParts = useMemo(() => moText.split(CITATION_REGEX_GLOBAL), [moText]);

  const handleCitationClick = (fileId, fileName, pageNumber) => {
    if (!fileId || !pageNumber) return;
    setSelectedPage({
      fileId,
      fileName,
      pageNumber,
    });
    setShowPageModal(true);
  };

  const closePageModal = () => {
    setShowPageModal(false);
    setSelectedPage({
      fileId: null,
      fileName: null,
      pageNumber: null,
    });
  };

  return (
    <>
      <Modal show={show} onHide={onHide} size="xl" scrollable>
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center gap-2">
            Medical Opinion Evidence
            {autoMoData?.status && (
              <Badge bg={autoMoData.status === 'completed' ? 'success' : 'secondary'}>
                {autoMoData.status}
              </Badge>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!hasEvidenceData && (
            <Alert variant="warning" className="mb-0">
              No generated Medical Opinion evidence is available yet.
            </Alert>
          )}

          {hasEvidenceData && (
            <>
              <div className="mb-3 text-muted small">
                Generated:{' '}
                {formatDateTime(
                  autoMoData?.generatedAt ||
                  autoMoData?.completedAt ||
                  fallbackConversationData?.conversation?.updatedAt,
                )}
                {(autoMoData?.pdfFileName || autoMoData?.pdfFileId) ? ` | PDF: ${autoMoData?.pdfFileName || 'Generated'}` : ''}
              </div>

              {!!moText && (
                <div className="dbq-mo-content-wrapper mb-3">
                  <div className="dbq-mo-content-body">
                    {contentParts.map((part, index) => {
                      const citation = citations.find((item) => item.text === part);
                      if (!citation) {
                        return <ReactMarkdown key={index}>{part}</ReactMarkdown>;
                      }

                      if (!citation.fileId) {
                        return (
                          <span key={index} className="text-muted small me-1">
                            {citation.text}
                          </span>
                        );
                      }

                      return (
                        <button
                          key={index}
                          type="button"
                          className="dbq-mo-citation-link"
                          onClick={() =>
                            handleCitationClick(
                              citation.fileId,
                              citation.fileName,
                              citation.pageNumber,
                            )
                          }
                          title={`View ${citation.fileName} page ${citation.pageNumber}`}
                        >
                          <span className="dbq-mo-citation-icon">📄</span>
                          <span className="dbq-mo-citation-text">p.{citation.pageNumber}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {sources.length > 0 && (
                <div className="dbq-mo-sources-section">
                  <div className="dbq-mo-sources-title">Linked Evidence</div>
                  <div className="dbq-mo-sources-list">
                    {sources.map((source, index) => (
                      <div key={index} className="dbq-mo-source-item">
                        <span className="dbq-mo-source-name">{source.fileName}</span>
                        <span className="dbq-mo-source-pages">
                          {source.pages.map((page, pageIndex) => (
                            <button
                              key={pageIndex}
                              type="button"
                              className="dbq-mo-source-page-link"
                              onClick={() =>
                                handleCitationClick(
                                  page.fileId || source.fileId,
                                  source.fileName,
                                  page.pageNumber,
                                )
                              }
                              disabled={!page.fileId && !source.fileId}
                            >
                              p.{page.pageNumber}
                              {typeof page.score === 'number' ? ` (${page.score.toFixed(2)})` : ''}
                            </button>
                          ))}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </Modal.Body>
      </Modal>

      <PageContentModal
        show={showPageModal}
        onHide={closePageModal}
        claimId={claimId}
        fileId={selectedPage.fileId}
        fileName={selectedPage.fileName}
        pageNumber={selectedPage.pageNumber}
      />
    </>
  );
};

export default MoEvidenceModal;
